import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Laptop, Smartphone, Tablet, Tv, AlertCircle } from "lucide-react";
import { MobileAppHeader } from "@/components/mobile/MobileAppHeader";
import {
  MEmpty,
  MSearchField,
  MSegmented,
} from "@/components/mobile/ui/primitives";
import { useDevices } from "@/hooks/useDevices";
import type { Device } from "@/types/device";
import { cn } from "@/lib/utils";

type OsFilter = "all" | "macos" | "ios" | "ipados" | "tvos";

const FILTERS: Array<{ value: OsFilter; label: string }> = [
  { value: "all", label: "All" },
  { value: "macos", label: "Mac" },
  { value: "ios", label: "iOS" },
  { value: "ipados", label: "iPad" },
  { value: "tvos", label: "TV" },
];

function platformIcon(os: string) {
  const o = os.toLowerCase();
  if (o.includes("mac")) return <Laptop className="h-4 w-4" />;
  if (o.includes("ipad")) return <Tablet className="h-4 w-4" />;
  if (o.includes("tv")) return <Tv className="h-4 w-4" />;
  return <Smartphone className="h-4 w-4" />;
}

function matchesOs(d: Device, filter: OsFilter): boolean {
  if (filter === "all") return true;
  const o = (d.os_type || "").toLowerCase();
  if (filter === "macos") return o.includes("mac");
  if (filter === "ios") return o === "ios" || o.includes("iphone");
  if (filter === "ipados") return o.includes("ipad");
  if (filter === "tvos") return o.includes("tv");
  return false;
}

function lastSeenLabel(iso?: string): string {
  if (!iso) return "unknown";
  const d = new Date(iso).getTime();
  if (Number.isNaN(d)) return "unknown";
  const diff = Date.now() - d;
  const m = Math.floor(diff / 60000);
  if (m < 1) return "just now";
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}

function statusTone(d: Device): { dot: string; label: string; tone: string } {
  if (d.status === "active")
    return { dot: "var(--m-ok)", label: "Online", tone: "text-[color:var(--m-ok)]" };
  if (d.status === "pending")
    return {
      dot: "var(--m-warn)",
      label: "Pending",
      tone: "text-[color:var(--m-warn)]",
    };
  return {
    dot: "var(--m-crit)",
    label: "Offline",
    tone: "text-[color:var(--m-crit)]",
  };
}

export default function MobileDevicesPage() {
  const navigate = useNavigate();
  const { data: devices = [], statusCounts, isLoading, isError, error } = useDevices({
    pageSize: 100,
  });
  const [filter, setFilter] = useState<OsFilter>("all");
  const [query, setQuery] = useState("");

  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return devices.filter((d) => {
      if (!matchesOs(d, filter)) return false;
      if (!q) return true;
      return (
        d.name?.toLowerCase().includes(q) ||
        d.serial_number?.toLowerCase().includes(q) ||
        d.model?.toLowerCase().includes(q) ||
        d.username?.toLowerCase().includes(q)
      );
    });
  }, [devices, query, filter]);

  return (
    <div className="pb-6">
      <MobileAppHeader />
      <div className="px-4">
        <div className="mb-3">
          <h1 className="m-h2">Devices</h1>
          <p className="m-sub">
            {statusCounts.total} managed · {statusCounts.active} online
          </p>
        </div>

        <MSearchField value={query} onChange={setQuery} placeholder="Search devices" />

        <div className="mb-3">
          <MSegmented
            value={filter}
            onChange={(v) => setFilter(v as OsFilter)}
            options={FILTERS}
          />
        </div>

        {isError ? (
          <MEmpty
            title="Couldn't load devices"
            description={(error as Error)?.message}
            icon={<AlertCircle className="h-4 w-4" />}
          />
        ) : isLoading && !devices.length ? (
          <div className="space-y-2">
            {Array.from({ length: 6 }).map((_, i) => (
              <div
                key={i}
                className="m-card flex items-center gap-3 p-3 opacity-50"
              >
                <div className="h-9 w-9 rounded-[11px] bg-[color:var(--m-fill-2)]" />
                <div className="flex-1 space-y-1.5">
                  <div className="h-2.5 w-1/2 rounded bg-[color:var(--m-fill-2)]" />
                  <div className="h-2 w-1/3 rounded bg-[color:var(--m-fill-2)]" />
                </div>
              </div>
            ))}
          </div>
        ) : visible.length === 0 ? (
          <MEmpty title="No devices match" description="Try clearing filters." />
        ) : (
          <div className="m-card divide-y divide-[color:var(--m-line)] overflow-hidden p-0">
            {visible.map((d) => {
              const s = statusTone(d);
              return (
                <button
                  key={d.id}
                  type="button"
                  onClick={() => navigate(`/m/devices/${d.id}`)}
                  className="flex w-full items-center gap-3 px-3.5 py-3 text-left tap-feedback"
                >
                  <span className="grid h-9 w-9 shrink-0 place-items-center rounded-[11px] bg-[color:var(--m-fill-2)] text-[color:var(--m-main,#333)]">
                    {platformIcon(d.os_type || "")}
                  </span>
                  <span className="min-w-0 flex-1">
                    <span className="block truncate text-[13px] font-semibold text-[color:var(--m-ink)]">
                      {d.name}
                    </span>
                    <span className="block truncate text-[11px] text-[color:var(--m-gray)]">
                      {d.model || d.os_type}
                      {d.serial_number ? (
                        <>
                          {" · "}
                          <code className="m-mono text-[10.5px]">
                            {d.serial_number}
                          </code>
                        </>
                      ) : null}
                    </span>
                  </span>
                  <span className="shrink-0 text-right">
                    <span className={cn("inline-flex items-center gap-1.5 text-[11px] font-semibold", s.tone)}>
                      <span
                        className="h-1.5 w-1.5 rounded-full"
                        style={{ backgroundColor: s.dot }}
                      />
                      {s.label}
                    </span>
                    <span className="mt-0.5 block text-[10.5px] text-[color:var(--m-gray-3)]">
                      {lastSeenLabel(d.last_seen)}
                    </span>
                  </span>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
